import {useContext, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';

import Select from './select/Select';
import _axios from '../utils/axios';
import validate from '../utils/validator';
import {handleError, USER_TYPES} from '../utils/utils';
import {DEFAULT_GROUP} from '../utils/settings';
import enabledImg from '../assets/user-solid-50-anti-flesh-white-100.png';
import disabledImg from '../assets/user-solid-50-anti-flesh-white-50.png';
import editImg from '../assets/pen-solid-cultured-50.png';
import deleteImg from '../assets/trash-solid-cultured-50.png';

import styles from '../styles/base_components/UserCard.module.scss';
import buttonStyles from '../styles/base_components/Button.module.scss';

const Wrapper = styled.div`
  &&& p:not(p:last-child) {
    color: ${props => props.enabled ? '#F3F6F7' : 'rgba(243, 246, 247, 0.5)'}
  }
`;

const Toggle = styled.div`
  &&& {
    justify-content: ${props => props.toggle ? 'flex-end' : 'flex-start'};
  }
`;

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(40, 40, 64, 0.5)'
  },
  content: {
    width: '80vw',
    minHeight: '50vh',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    border: 'none',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#303253'
  },
};

/**
 * @param {string} props.id
 * @param {Object} props.context
 */
function UserCard(props) {
  const context = useContext(props.context);
  const [user, setUser] = useState(context.users[props.id]);
  const [roles, setRoles] = useState([]);

  const [modal, setModal] = useState(false);
  const [items, setItems] = useState({});
  const [placeholder, setPlaceholder] = useState('');
  const [rolesError, setRolesError] = useState('');

  const [toggle, setToggle] = useState(false);

  function setDefaultItems() {
    let i = {};
    if (context.defaultGroup === DEFAULT_GROUP) {
      for (let group in context.groups) {
        for (let type of USER_TYPES) {
          i[group + ' ' + type] = !!user.roles.find(role => role === group + '_' + type);
        }
      }
    } else {
      for (let group of context.groups) {
        for (let type of USER_TYPES) {
          i[group + ' ' + type] = !!user.roles.find(role => role === group + '_' + type);
        }
      }
    }
    setItems(i);
    setPlaceholder(Object.keys(i).filter(item => i[item]).join(', '));
  }

  function toggleEnabled(event) {
    event.stopPropagation();
    setToggle(toggle => !toggle);
    if (modal) return;

    let data = {
      id: props.id,
      enabled: !user.enabled
    };
    if (context.defaultGroup !== DEFAULT_GROUP) data.group = context.defaultGroup;
    _axios.put(context.role.USERS_PATH, data)
      .then(response => {
        const {ok, data} = response.data;
        const {id, ...user} = data;
        // TODO validate response

        context.setUsers(users => {
          let newUsers = {...users};
          newUsers[id] = user;
          return newUsers;
        });

        setUser(user);
      })
      .catch(handleError);
  }

  function onCloseModal(event) {
    event.stopPropagation();
    setModal(false);
  }

  function editUser(event) {
    event.stopPropagation();
    setModal(true);
  }

  function validateUser(response) {
    const {ok, data} = response.data;
    if (typeof ok !== 'number' || typeof data !== 'object') throw new Error('The database does not correspond to our standard.');
    if (ok !== 1) throw new Error('The database is currently unavailable.');

    let err = validate([
      {value: data.id, type: 'db_user_id'},
      {value: data.username, type: 'db_user_username'},
      {value: data.email, type: 'db_user_email'},
      {value: data.groups, type: 'db_user_groups'},
      {value: data.roles, type: 'db_user_roles'},
      {value: data.enabled, type: 'db_user_enabled', empty: true}
    ]);
    err = err.filter(e => e !== '');
    if (err.length !== 0) throw new Error('Error at validating ' + data.host + ': ' + err.join('. '));
  }

  function saveChanges(event) {
    event.stopPropagation();

    let g = [];
    let r = [];
    for (let item in items) {
      if (!items[item]) continue;

      const [group, type] = item.split(' ');
      g.push(group);
      r.push(group + '_' + type);
    }

    const data = {
      id: props.id,
      groups: g.filter((value, i, array) => array.indexOf(value) === i),
      roles: r
    };
    if (context.defaultGroup !== DEFAULT_GROUP) data.group = context.defaultGroup;
    _axios.put(context.role.USERS_PATH, data)
      .then(response => {
        validateUser(response);

        context.setUsers(users => {
          let newUsers = {...users};
          newUsers[props.id].groups = [...response.data.data.groups];
          newUsers[props.id].roles = [...response.data.data.roles];
          return newUsers;
        });

        setModal(false);
      })
      .catch(handleError);
  }

  function cancel(event) {
    event.stopPropagation();
    setModal(false);
    setRolesError('');
    setDefaultItems();
  }

  function deleteUser(event) {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete ' + user.username + '?')) return;

    const config = {
      data: {id: props.id}
    };
    if (context.defaultGroup !== DEFAULT_GROUP) config.data.group = context.defaultGroup;
    _axios.delete(context.role.USERS_PATH, config)
      .then(response => {
        context.setUsers(users => {
          let newUsers = {...users};
          delete newUsers[props.id];
          return newUsers;
        });
      })
      .catch(handleError);
  }

  useEffect(() => {
    let i = {};
    if (context.defaultGroup === DEFAULT_GROUP) {
      for (let group in context.groups) {
        for (let type of USER_TYPES) {
          i[group + ' ' + type] = !!user.roles.find(role => role === group + '_' + type);
        }
      }
    } else {
      for (let group of context.groups) {
        for (let type of USER_TYPES) {
          i[group + ' ' + type] = !!user.roles.find(role => role === group + '_' + type);
        }
      }
    }
    setItems(i);
    setPlaceholder(Object.keys(i).filter(item => i[item]).join(', '));
  }, [context.defaultGroup, context.groups, user?.roles]);

  useEffect(() => {
    let r = [];
    for (let group of user.groups) {
      let g = group.charAt(0).toUpperCase() + group.slice(1);
      if (user.roles.find(role => role === group + '_admin')) {
        r.push(g + ' Admin');
      } else {
        r.push(g);
      }
    }
    setRoles(r);
  }, [user?.groups, user?.roles]);

  return (
    <Wrapper
      className={styles.userCard}
      enabled={user.enabled}
    >
      <div className={styles.header}>
        <Toggle className={styles.toggle} onClick={toggleEnabled} toggle={toggle} title="Enable/Disable user">
          <div/>
        </Toggle>
        {toggle ? 'User DISABLED' : ''}

        <div className={styles.edit}>
          <img src={editImg} alt='Pen Solid Cultured 50' onClick={editUser}/>
          <img src={deleteImg} alt='Trash Alt Solid Cultured 50' onClick={deleteUser}/>
        </div>
      </div>
      <div className={styles.content}>
        <img src={user.enabled ? enabledImg : disabledImg} alt='User solid 50px wide anti flesh white'/>
        <p>{user.username}</p>
        <p>{user.email}</p>
        <p>{roles.join(', ')}</p>
      </div>

      <Modal
        isOpen={modal}
        onRequestClose={onCloseModal}
        style={customStyles}
      >
        <div className={styles.modal}>
          <div className={styles.close} onClick={onCloseModal}/>

          <div>
            <p>USERNAME</p>
            <p>{user.username}</p>
          </div>
          <div>
            <p>EMAIL</p>
            <p>{user.email}</p>
          </div>

          <div className={styles.groups}>
            <Select
              className={styles.select}
              placeholder={placeholder ? placeholder : 'Enter group name'}
              setPlaceholder={setPlaceholder}
              items={items}
              setItems={setItems}
              error={rolesError}
              setError={setRolesError}
            />
            <div className={styles.buttons}>
              <button className={buttonStyles.button} onClick={saveChanges}>save changes</button>
              <button className={buttonStyles.button} onClick={cancel}>cancel</button>
            </div>
          </div>
        </div>
      </Modal>
    </Wrapper>
  );
}

export default UserCard;